import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

import { Checkbox } from '../../../components/ui/Checkbox';

const CandidateTable = ({ candidates, selectedCandidates, onSelectCandidate, onSelectAll, onStatusChange, onViewProfile, onScheduleInterview, onSendMessage }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const statusOptions = [
    { value: 'new', label: 'New Application' },
    { value: 'screening', label: 'Under Screening' },
    { value: 'shortlisted', label: 'Shortlisted' },
    { value: 'interview_scheduled', label: 'Interview Scheduled' },
    { value: 'interviewed', label: 'Interviewed' },
    { value: 'offered', label: 'Offer Extended' },
    { value: 'hired', label: 'Hired' },
    { value: 'rejected', label: 'Rejected' }
  ];

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-800 border-blue-200',
      screening: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      shortlisted: 'bg-purple-100 text-purple-800 border-purple-200',
      interview_scheduled: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      interviewed: 'bg-cyan-100 text-cyan-800 border-cyan-200',
      offered: 'bg-green-100 text-green-800 border-green-200',
      hired: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      rejected: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getRatingStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Icon
        key={index}
        name="Star"
        size={14}
        color={index < rating ? '#F59E0B' : '#D1D5DB'}
        className={index < rating ? 'fill-amber-500' : ''}
      />
    ));
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig?.key !== key) {
      return <Icon name="ChevronsUpDown" size={14} className="text-muted-foreground" />;
    }
    return sortConfig?.direction === 'asc' ? 
      <Icon name="ChevronUp" size={14} className="text-primary" /> : 
      <Icon name="ChevronDown" size={14} className="text-primary" />;
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-muted border-b border-border">
          <tr>
            <th className="px-4 py-3 text-left">
              <Checkbox
                checked={selectedCandidates?.length === candidates?.length}
                onChange={(e) => onSelectAll(e?.target?.checked)}
              />
            </th>
            <th className="px-4 py-3 text-left">
              <button
                onClick={() => handleSort('name')}
                className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-smooth"
              >
                Candidate
                {getSortIcon('name')}
              </button>
            </th>
            <th className="px-4 py-3 text-left">
              <button
                onClick={() => handleSort('position')}
                className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-smooth"
              >
                Position
                {getSortIcon('position')}
              </button>
            </th>
            <th className="px-4 py-3 text-left">
              <button
                onClick={() => handleSort('skillMatch')}
                className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-smooth"
              >
                Match
                {getSortIcon('skillMatch')}
              </button>
            </th>
            <th className="px-4 py-3 text-left">
              <button
                onClick={() => handleSort('rating')}
                className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-smooth"
              >
                Rating
                {getSortIcon('rating')}
              </button>
            </th>
            <th className="px-4 py-3 text-left">
              <button
                onClick={() => handleSort('appliedDate')}
                className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-smooth"
              >
                Applied
                {getSortIcon('appliedDate')}
              </button>
            </th>
            <th className="px-4 py-3 text-left">
              <span className="text-sm font-semibold text-foreground">Status</span>
            </th>
            <th className="px-4 py-3 text-left">
              <span className="text-sm font-semibold text-foreground">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {candidates?.map((candidate) => (
            <tr key={candidate?.id} className="hover:bg-muted/50 transition-smooth">
              <td className="px-4 py-3">
                <Checkbox
                  checked={selectedCandidates?.includes(candidate?.id)}
                  onChange={(e) => onSelectCandidate(candidate?.id, e?.target?.checked)}
                />
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-muted">
                    <Image
                      src={candidate?.avatar}
                      alt={candidate?.avatarAlt}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{candidate?.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{candidate?.location}</p>
                  </div>
                </div>
              </td>
              <td className="px-4 py-3">
                <p className="text-sm text-foreground">{candidate?.position}</p>
                <p className="text-xs text-muted-foreground">{candidate?.experience}</p>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-1">
                  <Icon name="Target" size={14} className="text-primary" />
                  <span className="text-sm font-medium text-foreground">{candidate?.skillMatch}%</span>
                </div>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-1">
                  {getRatingStars(candidate?.rating)}
                </div>
              </td>
              <td className="px-4 py-3">
                <p className="text-sm text-foreground">{candidate?.appliedDate}</p>
              </td>
              <td className="px-4 py-3">
                <div className={`inline-flex px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(candidate?.status)}`}>
                  {statusOptions?.find(opt => opt?.value === candidate?.status)?.label}
                </div>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    iconName="Eye"
                    onClick={() => onViewProfile(candidate?.id)}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    iconName="Calendar"
                    onClick={() => onScheduleInterview(candidate?.id)}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    iconName="MessageSquare"
                    onClick={() => onSendMessage(candidate?.id)}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CandidateTable;